import clsx from 'clsx';
import * as React from 'react';
import { useRecoilState } from 'recoil';

import { Icon } from '@gitbook/spine-icons';
import {
    AnimatedStateContext,
    Button,
    Overlay,
    OverlayCloseButton,
    OverlayRoot,
    Portal,
    useOverlayClose,
} from '@gitbook/spine-react';

import { useKeyboardShortcuts } from '../../Keyboard';
import { getImageResizedURL } from '../../utils';
import { type MediaGalleryItem, mediaGalleryAtom, mediaGalleryDefaultState } from './state';
/**
 * Overlay portal to show images zoomed.
 */
export function MediaGallery() {
    const [state, setState] = useRecoilState(mediaGalleryAtom);

    return (
        <OverlayRoot
            isOpen={state.medias.length > 0}
            onOpenChange={(isOpen) => {
                if (!isOpen) {
                    setState(mediaGalleryDefaultState);
                }
            }}
        >
            <MediaGalleryModal>
                <MediaGalleryContent medias={state.medias} initialIndex={state.initialIndex} />
            </MediaGalleryModal>
        </OverlayRoot>
    );
}

function MediaGalleryModal(props: { children: React.ReactNode }) {
    const animatedState = React.useContext(AnimatedStateContext);

    if (animatedState === 'exited') {
        return null;
    }

    return (
        <Portal>
            <Overlay
                className={clsx(
                    'fixed inset-0 z-[100] flex items-center justify-center bg-overlay bg-opacity-90 backdrop-blur-2xl hover:cursor-zoom-out',
                    'fill-mode-forwards transition [transition-delay:30ms] [transition-duration:150ms] [transition-timing-function:ease-out]',
                    animatedState === 'entering' && 'fade-in zoom-in-95 animate-in',
                    animatedState === 'exiting' && 'fade-out zoom-out-95 animate-out'
                )}
            >
                {props.children}
            </Overlay>
        </Portal>
    );
}

const SIZES = [480, 768, 1024, 1440, 1920, 2400];

function MediaGalleryContent(props: { medias: MediaGalleryItem[]; initialIndex: number }) {
    const { initialIndex } = props;
    const medias = useLatestMedias(props.medias);
    const closeOverlay = useOverlayClose();
    const [currentIndex, setCurrentIndex] = React.useState(initialIndex);
    const goPrevious = () => setCurrentIndex((prev) => Math.max(prev - 1, 0));
    const goNext = () => setCurrentIndex((prev) => Math.min(prev + 1, medias.length - 1));

    useKeyboardShortcuts({ Left: goPrevious, Right: goNext }, 'all');

    const imageUrl = medias[currentIndex].url;

    return (
        // biome-ignore lint/a11y/useKeyWithClickEvents: it's fine, we just want to close when clicking
        <div
            className="relative z-10 flex size-full items-center justify-center px-12 pt-4 pb-10"
            onClick={() => {
                closeOverlay();
            }}
        >
            <img
                src={imageUrl}
                srcSet={SIZES.map(
                    (size) => `${getImageResizedURL(imageUrl, { width: size })} ${size}w`
                ).join(', ')}
                sizes="100vw"
                alt={medias.length > 1 ? `Media #${currentIndex + 1}` : ''}
                className="max-h-full max-w-full object-contain"
            />

            {medias.length > 1 && (
                <span className="-translate-x-1/2 absolute bottom-2 left-1/2 flex gap-1 font-medium text-lg text-white tabular-nums">
                    {currentIndex + 1}
                    <span className="opacity-disabled">of</span>
                    {medias.length}
                </span>
            )}
            <OverlayCloseButton
                kind="blank"
                label="Close"
                leadingIcon={Icon.X}
                iconOnly
                className="absolute top-2 right-2"
            />

            {medias.length > 1 && (
                <Button
                    kind="blank"
                    label="Previous"
                    leadingIcon={Icon.ChevronLeft}
                    iconOnly
                    onPress={goPrevious}
                    disabled={currentIndex === 0}
                    className="-translate-y-1/2 absolute top-1/2 left-2 z-10 transform disabled:bg-transparent"
                />
            )}
            {medias.length > 1 && (
                <Button
                    kind="blank"
                    label="Next"
                    leadingIcon={Icon.ChevronRight}
                    iconOnly
                    onPress={goNext}
                    disabled={currentIndex === medias.length - 1}
                    className="-translate-y-1/2 absolute top-1/2 right-2 z-10 transform disabled:bg-transparent"
                />
            )}
        </div>
    );
}

/**
 * Preserve the latest medias, so during the closing of the modal, we can still
 * access the latest medias.
 */
function useLatestMedias(medias: MediaGalleryItem[]) {
    const snapshotRef = React.useRef<MediaGalleryItem[]>(medias);
    React.useLayoutEffect(() => {
        if (medias.length > 0) {
            snapshotRef.current = medias;
        }
    }, [medias]);
    return medias.length ? medias : snapshotRef.current;
}
